// SBIS Consulting — Kontrolna tabla
// 4 sekcije: Nedeljni, Knjiženja, Finansijski pregled, Aktivnost

import { state } from './state.js';
import { sb } from './supabase.js';
import { fNum } from './utils.js';
import Chart from 'chart.js/auto';

let catChartInst = null;
let barChartInst = null, typeChartInst = null, cashChartInst = null;
let savedReports = [], kjData = [], kjDetail = [];
let finBsData = null, finBs12 = null; // financial snapshot + 12m data

const MONTHS = ['Jan','Feb','Mar','Apr','Maj','Jun','Jul','Avg','Sep','Okt','Nov','Dec'];
const CAT_COLORS = ['#ff5c6a','#c9a84c','#6398ff','#00e89d','#ffb547','#a78bfa','#ffd866','#ff8c42','#34d399','#f472b6','#82b1ff','#e0c872'];

export async function renderDashboard() {
  await Promise.all([loadSavedReports(), initKjSelectors()]);
  await Promise.all([loadKjData(), loadKjDetail(), loadFinancialData()]);
  // Nedeljni
  renderNedKpi();
  renderCatChart();
  renderAgingChart();
  // Knjiženja
  renderKjKpi();
  renderBarChart();
  renderTypeChart();
  renderMonthDetail();
  // Finansijski pregled
  renderFinKpis();
  renderCashTrend();
  renderTop10('dashTop10Supp', '435,436', 'Dobavljači (435/436)', 'var(--dng)');
  renderTop10('dashTop10Cust', '204,205', 'Kupci (204/205)', 'var(--suc)');
  // Aktivnost
  renderActivity();
}

// ============================================================
// DATA LOADING
// ============================================================
async function loadSavedReports() {
  savedReports = [];
  if (!state.CU) return;
  try {
    const { data, error } = await sb.rpc('get_saved_reports', { p_user_id: state.CU.id });
    if (!error && data) savedReports = data;
  } catch (e) { console.error('Dashboard reports:', e); }
}

async function initKjSelectors() {
  const yearSel = document.getElementById('dashYear');
  const monthSel = document.getElementById('dashMonth');
  if (!yearSel || !monthSel) return;
  let years = [];
  try {
    if (state.CU) {
      const { data } = await sb.rpc('get_knjizenja_years', { p_user_id: state.CU.id });
      if (data) years = data.map(r => r.year_num);
    }
  } catch (e) { console.error('Dashboard years:', e); }
  if (!years.length) years = [new Date().getFullYear()];
  const curY = yearSel.value, curM = monthSel.value;
  if (!yearSel.options.length || yearSel.dataset.init !== 'done') {
    yearSel.textContent = '';
    years.forEach(y => { const o = document.createElement('option'); o.value = y; o.textContent = y; yearSel.appendChild(o); });
    yearSel.dataset.init = 'done';
    if (curY && years.includes(parseInt(curY))) yearSel.value = curY;
  }
  if (!monthSel.options.length || monthSel.dataset.init !== 'done') {
    monthSel.textContent = '';
    const oAll = document.createElement('option'); oAll.value = '0'; oAll.textContent = 'Svi meseci'; monthSel.appendChild(oAll);
    MONTHS.forEach((m, i) => { const o = document.createElement('option'); o.value = i + 1; o.textContent = m; monthSel.appendChild(o); });
    monthSel.dataset.init = 'done';
    if (curM) monthSel.value = curM;
  }
}

async function loadKjData() {
  kjData = [];
  if (!state.CU) return;
  const year = parseInt(document.getElementById('dashYear')?.value) || new Date().getFullYear();
  try {
    const { data, error } = await sb.rpc('get_knjizenja_dashboard', { p_user_id: state.CU.id, p_year: year });
    if (!error) kjData = data || [];
  } catch (e) { console.error('Dashboard kj data:', e); }
}

async function loadKjDetail() {
  kjDetail = [];
  if (!state.CU) return;
  const year = parseInt(document.getElementById('dashYear')?.value) || new Date().getFullYear();
  try {
    const { data, error } = await sb.rpc('get_knjizenja_detail', { p_user_id: state.CU.id, p_year: year });
    if (!error) kjDetail = data || [];
  } catch (e) { console.error('Dashboard kj detail:', e); }
}

function getKjFiltered() {
  const m = parseInt(document.getElementById('dashMonth')?.value) || 0;
  return m > 0 ? kjData.filter(r => r.month_num === m) : kjData;
}

function getLatestNedeljni() {
  return savedReports.find(r => r.report_type === 'nedeljni') || null;
}

function getLatestGroups() {
  const r = getLatestNedeljni();
  const dj = r && r.details_json;
  if (!dj) return [];
  // Novi oblik: { dospelo:[...], nedospelo:[...] }; stari: niz (samo dospelo)
  return Array.isArray(dj) ? dj : (Array.isArray(dj.dospelo) ? dj.dospelo : []);
}

// ============================================================
// SEKCIJA 1: NEDELJNI IZVEŠTAJI
// ============================================================
function renderNedKpi() {
  const bar = document.getElementById('dashNedKpi'); if (!bar) return;
  bar.textContent = '';
  const latest = getLatestNedeljni();
  if (!latest) {
    const p = document.createElement('div'); p.style.cssText = 'grid-column:1/-1;color:var(--tx3);font-size:13px;text-align:center;padding:16px';
    p.textContent = 'Nema sačuvanih nedeljnih izveštaja.'; bar.appendChild(p); return;
  }
  const totalRsd = parseFloat(latest.total_rsd) || 0;
  const totalUsd = parseFloat(latest.total_usd) || 0;
  const kurs = parseFloat(latest.kurs) || 1;
  const groups = getLatestGroups();
  const komCount = groups.reduce((s, g) => s + (g.items || []).length, 0);
  const fmtD = s => new Date(s).toLocaleDateString('sr-RS');
  const period = fmtD(latest.period_from) + ' \u2014 ' + fmtD(latest.period_to);

  [{l:'Ukupno RSD',v:fNum(totalRsd),c:'var(--dng)',sub:period},
   {l:'Ukupno USD',v:fNum(totalUsd),c:'var(--acc)',sub:period},
   {l:'Kurs',v:'1 USD = '+fNum(kurs)+' RSD',c:'var(--tx)',sub:''},
   {l:'Komitenata',v:komCount,c:'var(--acc)',sub:groups.length+' kategorija'},
   {l:'Izveštaja',v:savedReports.length,c:'var(--tx2)',sub:'sačuvano'}
  ].forEach(s => {
    const card = document.createElement('div'); card.className = 'card';
    card.style.cssText = 'padding:14px 16px;display:flex;flex-direction:column;gap:3px';
    const lbl = document.createElement('div'); lbl.style.cssText = 'font-size:10px;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px'; lbl.textContent = s.l;
    const val = document.createElement('div'); val.style.cssText = 'font-size:18px;font-weight:700;font-family:var(--mn)'; val.textContent = s.v; if (s.c) val.style.color = s.c;
    const sub = document.createElement('div'); sub.style.cssText = 'font-size:10px;color:var(--tx3)'; sub.textContent = s.sub || '';
    card.appendChild(lbl); card.appendChild(val); card.appendChild(sub); bar.appendChild(card);
  });
}

function renderCatChart() {
  const canvas = document.getElementById('dashCatChart'); if (!canvas) return;
  const groups = getLatestGroups();
  if (catChartInst) catChartInst.destroy(); catChartInst = null;
  if (!groups.length) return;
  const sorted = [...groups].sort((a, b) => b.total - a.total);
  const grandTotal = sorted.reduce((s, g) => s + g.total, 0);
  const labels = sorted.map(g => {
    const pct = grandTotal > 0 ? ((g.total / grandTotal) * 100).toFixed(1) : '0';
    return (g.group_sr || g.group) + '  (' + pct + '%)';
  });
  catChartInst = new Chart(canvas, {
    type: 'bar', data: { labels, datasets: [{ label: 'RSD', data: sorted.map(g => g.total), backgroundColor: CAT_COLORS.slice(0, sorted.length), borderRadius: 4 }] },
    options: { responsive: true, maintainAspectRatio: false, indexAxis: 'y',
      scales: { x: { beginAtZero: true, ticks: { color: '#5d6180', callback: v => v>=1e6?(v/1e6).toFixed(1)+'M':v>=1e3?(v/1e3).toFixed(0)+'K':v }, grid: { color: 'rgba(30,32,48,.5)' } }, y: { ticks: { color: '#9da1b8', font: { size: 11 } }, grid: { display: false } } },
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => { const g = sorted[ctx.dataIndex]; const pct = grandTotal>0?((g.total/grandTotal)*100).toFixed(1):'0'; return fNum(ctx.parsed.x)+' RSD ('+pct+'%)'; } } } } }
  });
}

function renderAgingChart() {
  const box = document.getElementById('dashAgingChart'); if (!box) return;
  // Use DOM instead of canvas for this chart
  const parent = box.parentElement;
  box.remove();
  const container = document.createElement('div'); container.id = 'dashAgingChart';
  parent.appendChild(container);

  const kasnReport = savedReports.find(r => r.report_type === 'kasnjenja' && r.details_json);
  if (!kasnReport) {
    container.style.cssText = 'color:var(--tx3);font-size:13px;text-align:center;padding:40px';
    container.textContent = 'Sačuvajte izveštaj na stranici "Dani kašnjenja".'; return;
  }
  const invoices = kasnReport.details_json || [];
  if (!invoices.length) return;

  const buckets = [
    { label: '0\u201315 dana', min: 0, max: 15, color: '#00e89d', count: 0, total: 0 },
    { label: '16\u201330 dana', min: 16, max: 30, color: '#c9a84c', count: 0, total: 0 },
    { label: '31\u201360 dana', min: 31, max: 60, color: '#ffb547', count: 0, total: 0 },
    { label: '61\u201390 dana', min: 61, max: 90, color: '#ff8c42', count: 0, total: 0 },
    { label: '90+ dana', min: 91, max: 99999, color: '#ff5c6a', count: 0, total: 0 }
  ];

  let totalAmount = 0;
  invoices.forEach(r => {
    const dk = r.dk || 0, dos = r.dos || 0;
    if (dos <= 0) return;
    totalAmount += dos;
    const b = buckets.find(b => dk >= b.min && dk <= b.max);
    if (b) { b.count++; b.total += dos; }
  });

  const maxTotal = Math.max(...buckets.map(b => b.total), 1);

  // Stacked overview bar
  const stackBar = document.createElement('div'); stackBar.style.cssText = 'display:flex;height:28px;border-radius:6px;overflow:hidden;margin-bottom:20px';
  buckets.forEach(b => {
    if (b.total <= 0) return;
    const seg = document.createElement('div');
    const pct = (b.total / totalAmount * 100);
    seg.style.cssText = 'height:100%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:#fff;min-width:' + Math.max(pct, 3) + '%;background:' + b.color;
    seg.style.flex = pct.toString();
    if (pct > 8) seg.textContent = pct.toFixed(0) + '%';
    seg.title = b.label + ': ' + fNum(b.total) + ' RSD (' + pct.toFixed(1) + '%)';
    stackBar.appendChild(seg);
  });
  container.appendChild(stackBar);

  // Individual rows with progress bars
  buckets.forEach(b => {
    const pct = totalAmount > 0 ? (b.total / totalAmount * 100).toFixed(1) : '0';
    const barPct = maxTotal > 0 ? (b.total / maxTotal * 100) : 0;

    const row = document.createElement('div'); row.style.cssText = 'display:flex;align-items:center;gap:12px;margin-bottom:10px';
    const lbl = document.createElement('div'); lbl.style.cssText = 'width:90px;font-size:12px;font-weight:600;color:' + b.color; lbl.textContent = b.label;
    const barWrap = document.createElement('div'); barWrap.style.cssText = 'flex:1;height:20px;background:var(--surface);border-radius:4px;overflow:hidden;position:relative';
    const bar = document.createElement('div'); bar.style.cssText = 'height:100%;border-radius:4px;background:' + b.color + ';width:' + barPct + '%;transition:width .5s ease;opacity:.8';
    barWrap.appendChild(bar);
    const vals = document.createElement('div'); vals.style.cssText = 'width:160px;text-align:right;font-family:var(--mn);font-size:13px;font-weight:600;color:' + b.color;
    vals.textContent = fNum(b.total);
    const meta = document.createElement('div'); meta.style.cssText = 'width:80px;text-align:right;font-size:11px;color:var(--tx3)';
    meta.textContent = b.count + ' st. (' + pct + '%)';
    row.appendChild(lbl); row.appendChild(barWrap); row.appendChild(vals); row.appendChild(meta);
    container.appendChild(row);
  });

  // Total
  const totRow = document.createElement('div'); totRow.style.cssText = 'display:flex;justify-content:space-between;padding-top:10px;border-top:1px solid var(--border);margin-top:6px;font-weight:700;font-size:13px';
  const totL = document.createElement('span'); totL.style.color = 'var(--tx2)'; totL.textContent = 'Ukupno: ' + invoices.filter(r => r.dos > 0).length + ' stavki';
  const totR = document.createElement('span'); totR.style.cssText = 'font-family:var(--mn);color:var(--dng)'; totR.textContent = fNum(totalAmount) + ' RSD';
  totRow.appendChild(totL); totRow.appendChild(totR);
  container.appendChild(totRow);
}

function renderSavedReportsList() {
  const box = document.getElementById('dashReportsList'); if (!box) return;
  box.textContent = '';
  if (!savedReports.length) { const p = document.createElement('div'); p.style.cssText = 'color:var(--tx3);font-size:13px;text-align:center;padding:16px'; p.textContent = 'Nema sačuvanih izveštaja.'; box.appendChild(p); return; }
  const fmtD = s => new Date(s).toLocaleDateString('sr-RS');
  savedReports.forEach(rep => {
    const dj = rep.details_json; const groups = Array.isArray(dj) ? dj : (dj && Array.isArray(dj.dospelo) ? dj.dospelo : []);
    const kurs = parseFloat(rep.kurs)||1, totalRsd = parseFloat(rep.total_rsd)||0, totalUsd = parseFloat(rep.total_usd)||0;
    const item = document.createElement('div'); item.style.cssText = 'border:1px solid var(--border);border-radius:var(--rs);margin-bottom:6px;overflow:hidden';
    const hdr = document.createElement('div'); hdr.style.cssText = 'padding:10px 14px;display:flex;justify-content:space-between;align-items:center;cursor:pointer;background:var(--surface);transition:background .2s';
    hdr.onmouseenter = () => hdr.style.background='var(--hover)'; hdr.onmouseleave = () => hdr.style.background='var(--surface)';
    const hdrL = document.createElement('div');
    const period = document.createElement('span'); period.style.cssText = 'font-weight:600;font-size:12px;color:var(--tx)'; period.textContent = fmtD(rep.period_from)+' \u2014 '+fmtD(rep.period_to);
    const date = document.createElement('span'); date.style.cssText = 'font-size:10px;color:var(--tx3);margin-left:8px'; date.textContent = new Date(rep.created_at).toLocaleDateString('sr-RS');
    hdrL.appendChild(period); hdrL.appendChild(date);
    const hdrR = document.createElement('div'); hdrR.style.cssText = 'display:flex;align-items:center;gap:12px';
    const tR = document.createElement('span'); tR.style.cssText = 'font-family:var(--mn);font-weight:700;color:var(--dng);font-size:13px'; tR.textContent = fNum(totalRsd)+' RSD';
    const tU = document.createElement('span'); tU.style.cssText = 'font-family:var(--mn);font-weight:600;color:var(--acc);font-size:12px'; tU.textContent = fNum(totalUsd)+' USD';
    const chev = document.createElement('span'); chev.style.cssText = 'color:var(--tx3);font-size:9px;transition:transform .2s'; chev.textContent = '\u25BC';
    hdrR.appendChild(tR); hdrR.appendChild(tU); hdrR.appendChild(chev);
    hdr.appendChild(hdrL); hdr.appendChild(hdrR);
    const body = document.createElement('div'); body.style.display = 'none';
    hdr.onclick = () => { const open = body.style.display !== 'none'; body.style.display = open?'none':'block'; chev.style.transform = open?'':'rotate(180deg)';
      if (!open && !body.dataset.loaded) { body.dataset.loaded = '1'; renderDetail(body, groups, kurs); } };
    item.appendChild(hdr); item.appendChild(body); box.appendChild(item);
  });
}

function renderDetail(container, groups, kurs) {
  if (!groups||!groups.length) { const p = document.createElement('div'); p.style.cssText = 'padding:14px;color:var(--tx3);font-size:12px'; p.textContent = 'Detaljni podaci nisu dostupni. Sačuvajte izveštaj ponovo.'; container.appendChild(p); return; }
  groups.sort((a,b) => b.total-a.total).forEach(g => {
    const gh = document.createElement('div'); gh.style.cssText = 'padding:8px 14px;background:var(--raised);border-bottom:1px solid var(--border);display:flex;justify-content:space-between;align-items:center';
    const gl = document.createElement('div'); gl.style.cssText = 'font-weight:600;font-size:12px;color:var(--tx)'; gl.textContent = (g.icon?g.icon+' ':'')+g.group+(g.group_sr?' \u2014 '+g.group_sr:'');
    const gr = document.createElement('div'); gr.style.cssText = 'display:flex;gap:10px;font-family:var(--mn);font-size:11px';
    const gR = document.createElement('span'); gR.style.cssText = 'color:var(--dng);font-weight:600'; gR.textContent = fNum(g.total)+' RSD';
    const gU = document.createElement('span'); gU.style.color = 'var(--acc)'; gU.textContent = fNum(g.total/kurs)+' USD';
    gr.appendChild(gR); gr.appendChild(gU); gh.appendChild(gl); gh.appendChild(gr); container.appendChild(gh);
    const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;font-size:11px;border-collapse:collapse';
    (g.items||[]).sort((a,b) => b.dospelo-a.dospelo).forEach(it => {
      const tr = document.createElement('tr'); tr.style.borderBottom = '1px solid var(--border-lt)';
      const td1 = document.createElement('td'); td1.style.cssText = 'padding:4px 14px;color:var(--tx)'; td1.textContent = it.komitent;
      const td2 = document.createElement('td'); td2.style.cssText = 'padding:4px 6px;text-align:right;font-family:var(--mn);color:var(--dng)'; td2.textContent = fNum(it.dospelo);
      const td3 = document.createElement('td'); td3.style.cssText = 'padding:4px 6px;text-align:right;font-family:var(--mn);color:var(--tx2)'; td3.textContent = fNum(it.dospelo/kurs);
      tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tbl.appendChild(tr);
    });
    container.appendChild(tbl);
  });
}

// ============================================================
// SEKCIJA 2: KNJIŽENJA
// ============================================================
function renderKjKpi() {
  const bar = document.getElementById('dashKjKpi'); if (!bar) return;
  bar.textContent = '';
  const filtered = getKjFiltered();
  const totalD = filtered.reduce((s,r) => s+parseFloat(r.total_debit||0), 0);
  const totalC = filtered.reduce((s,r) => s+parseFloat(r.total_credit||0), 0);
  const entries = filtered.reduce((s,r) => s+parseInt(r.entry_count||0), 0);
  const accounts = new Set(filtered.map(r => r.rs_code)).size;
  const selM = parseInt(document.getElementById('dashMonth')?.value)||0;
  const period = selM > 0 ? MONTHS[selM-1]+' '+(document.getElementById('dashYear')?.value||'') : 'Godina '+(document.getElementById('dashYear')?.value||'');

  [{l:'Ukupno Duguje',v:fNum(totalD)+' RSD',c:'var(--dng)',sub:period},
   {l:'Ukupno Potražuje',v:fNum(totalC)+' RSD',c:'var(--suc)',sub:period},
   {l:'Bilans',v:fNum(Math.abs(totalD-totalC))+' RSD',c:Math.abs(totalD-totalC)<1?'var(--suc)':'var(--warn)',sub:totalD>totalC?'Duguje više':totalC>totalD?'Potražuje više':'Balansirano'},
   {l:'Stavki / Konta',v:entries+' / '+accounts,c:'var(--acc)',sub:''}
  ].forEach(s => {
    const card = document.createElement('div'); card.className = 'card';
    card.style.cssText = 'padding:14px 16px;display:flex;flex-direction:column;gap:3px';
    const lbl = document.createElement('div'); lbl.style.cssText = 'font-size:10px;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px'; lbl.textContent = s.l;
    const val = document.createElement('div'); val.style.cssText = 'font-size:18px;font-weight:700;font-family:var(--mn)'; val.textContent = s.v; if(s.c) val.style.color = s.c;
    const sub = document.createElement('div'); sub.style.cssText = 'font-size:10px;color:var(--tx3)'; sub.textContent = s.sub||'';
    card.appendChild(lbl); card.appendChild(val); card.appendChild(sub); bar.appendChild(card);
  });
}

function renderBarChart() {
  const canvas = document.getElementById('dashBarChart'); if (!canvas) return;
  const monthlyD = new Array(12).fill(0), monthlyC = new Array(12).fill(0);
  kjData.forEach(r => { const m = r.month_num-1; if (m>=0&&m<12) { monthlyD[m]+=parseFloat(r.total_debit||0); monthlyC[m]+=parseFloat(r.total_credit||0); } });
  if (barChartInst) barChartInst.destroy();
  barChartInst = new Chart(canvas, {
    type: 'line', data: { labels: MONTHS, datasets: [
      { label: 'Duguje', data: monthlyD, borderColor: '#ff5c6a', backgroundColor: 'rgba(255,92,106,.1)', fill: true, tension: .3, pointRadius: 5, pointBackgroundColor: '#ff5c6a', pointBorderColor: '#0a0b10', pointBorderWidth: 2, borderWidth: 2.5 },
      { label: 'Potražuje', data: monthlyC, borderColor: '#00e89d', backgroundColor: 'rgba(0,232,157,.1)', fill: true, tension: .3, pointRadius: 5, pointBackgroundColor: '#00e89d', pointBorderColor: '#0a0b10', pointBorderWidth: 2, borderWidth: 2.5 }
    ] },
    options: { responsive: true, maintainAspectRatio: false, interaction: { intersect: false, mode: 'index' },
      scales: { y: { beginAtZero: true, ticks: { color: '#5d6180', font: { size: 12 }, callback: v => v>=1e6?(v/1e6).toFixed(1)+'M':v>=1e3?(v/1e3).toFixed(0)+'K':v }, grid: { color: 'rgba(30,32,48,.5)' } }, x: { ticks: { color: '#9da1b8', font: { size: 12 } }, grid: { display: false } } },
      plugins: { legend: { position: 'top', labels: { color: '#9da1b8', font: { size: 12 }, padding: 12, boxWidth: 14 } }, tooltip: { callbacks: { label: ctx => ctx.dataset.label+': '+fNum(ctx.parsed.y)+' RSD' } } } }
  });
}

function renderTypeChart() {
  const canvas = document.getElementById('dashTypeChart'); if (!canvas) return;
  const filtered = getKjFiltered();
  const byType = {};
  filtered.forEach(r => { const t = r.account_type||'Ostalo'; byType[t] = (byType[t]||0)+parseFloat(r.total_debit||0)+parseFloat(r.total_credit||0); });
  const labels = Object.keys(byType).filter(k => byType[k]>0).sort((a,b) => byType[b]-byType[a]);
  const data = labels.map(k => byType[k]);
  const colors = ['#ff5c6a','#c9a84c','#e0c872','#a78bfa','#ffb547','#00e89d','#34d399','#ffd866','#ff8c42','#5d6180'];
  if (typeChartInst) typeChartInst.destroy(); typeChartInst = null;
  if (!data.length) return;
  typeChartInst = new Chart(canvas, {
    type: 'doughnut', data: { labels, datasets: [{ data, backgroundColor: colors.slice(0,labels.length), borderWidth: 0 }] },
    options: { responsive: true, maintainAspectRatio: false, cutout: '55%',
      plugins: { legend: { position: 'right', labels: { color: '#9da1b8', font: { size: 10 }, padding: 5, boxWidth: 10 } }, tooltip: { callbacks: { label: ctx => ctx.label+': '+fNum(ctx.parsed)+' RSD' } } } }
  });
}

function renderMonthDetail() {
  const box = document.getElementById('dashMonthDetail'); if (!box) return;
  box.textContent = '';

  if (!kjDetail.length) { const p = document.createElement('div'); p.style.cssText = 'color:var(--tx3);font-size:13px;padding:16px 0;text-align:center'; p.textContent = 'Nema podataka za izabrani period.'; box.appendChild(p); return; }

  // Separate SUMMARY/PARENT and ANALYTIC rows
  const summaries = kjDetail.filter(r => r.detail_level === 'SUMMARY' || r.detail_level === 'PARENT');
  const analyticRows = kjDetail.filter(r => r.detail_level === 'ANALYTIC');

  // Aggregate SUMMARY rows by konto (they're the main entries)
  const byKonto = {};
  summaries.forEach(r => {
    const k = r.rs_code + '|' + r.detail_level;
    if (!byKonto[k]) byKonto[k] = { rs_code: r.rs_code, en_name: r.en_name, detail_level: r.detail_level, debit: 0, credit: 0 };
    byKonto[k].debit += parseFloat(r.total_debit || 0);
    byKonto[k].credit += parseFloat(r.total_credit || 0);
  });

  // Merge PARENT into SUMMARY if both exist for same konto
  const merged = {};
  Object.values(byKonto).forEach(r => {
    if (!merged[r.rs_code]) merged[r.rs_code] = { rs_code: r.rs_code, en_name: r.en_name, debit: 0, credit: 0, hasAnalytics: false };
    // Only count SUMMARY debit/credit (PARENT is the same amount shown differently)
    if (r.detail_level === 'SUMMARY') { merged[r.rs_code].debit += r.debit; merged[r.rs_code].credit += r.credit; }
    else if (r.detail_level === 'PARENT') {
      merged[r.rs_code].hasAnalytics = true;
      // If no SUMMARY exists for this konto, use PARENT amounts
      if (!merged[r.rs_code].debit && !merged[r.rs_code].credit) { merged[r.rs_code].debit += r.debit; merged[r.rs_code].credit += r.credit; }
    }
  });

  // Aggregate analytics by konto + analytic
  const analyticAgg = {};
  analyticRows.forEach(r => {
    const k = r.parent_code + '|' + r.analytic_code;
    if (!analyticAgg[k]) analyticAgg[k] = { parent_code: r.parent_code, analytic_code: r.analytic_code, analytic_name: r.analytic_name, debit: 0, credit: 0 };
    analyticAgg[k].debit += parseFloat(r.total_debit || 0);
    analyticAgg[k].credit += parseFloat(r.total_credit || 0);
  });

  const rows = Object.values(merged).sort((a, b) => (b.debit + b.credit) - (a.debit + a.credit));
  let grandD = 0, grandC = 0;

  const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;font-size:14px;border-collapse:collapse';
  const thead = document.createElement('thead'); const htr = document.createElement('tr');
  ['Konto', 'Naziv', 'Duguje', 'Potražuje'].forEach(h => {
    const th = document.createElement('th'); th.textContent = h;
    th.style.cssText = 'text-align:left;padding:8px 10px;border-bottom:2px solid var(--border);color:var(--tx3);font-size:12px;text-transform:uppercase;letter-spacing:.5px;position:sticky;top:0;background:var(--raised);z-index:1';
    if (h === 'Duguje' || h === 'Potražuje') th.style.textAlign = 'right';
    htr.appendChild(th);
  });
  thead.appendChild(htr); tbl.appendChild(thead);
  const tbody = document.createElement('tbody');

  rows.forEach(r => {
    grandD += r.debit; grandC += r.credit;
    const tr = document.createElement('tr'); tr.style.borderBottom = '1px solid var(--border-lt)';
    const td1 = document.createElement('td'); td1.style.cssText = 'padding:6px 10px;font-family:var(--mn);font-weight:700;color:var(--acc);font-size:14px'; td1.textContent = r.rs_code;
    const td2 = document.createElement('td'); td2.style.cssText = 'padding:6px 10px;color:var(--tx);font-size:14px'; td2.textContent = r.en_name || '';
    const td3 = document.createElement('td'); td3.style.cssText = 'padding:6px 10px;text-align:right;font-family:var(--mn);font-size:14px;font-weight:600';
    if (r.debit > 0) { td3.textContent = fNum(r.debit); td3.style.color = 'var(--dng)'; }
    const td4 = document.createElement('td'); td4.style.cssText = 'padding:6px 10px;text-align:right;font-family:var(--mn);font-size:14px;font-weight:600';
    if (r.credit > 0) { td4.textContent = fNum(r.credit); td4.style.color = 'var(--suc)'; }
    tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4);
    tbody.appendChild(tr);

    // Analytics sub-rows
    if (r.hasAnalytics) {
      const myAnalytics = Object.values(analyticAgg).filter(a => a.parent_code === r.rs_code).sort((a, b) => (b.debit + b.credit) - (a.debit + a.credit));
      myAnalytics.forEach(a => {
        const atr = document.createElement('tr'); atr.style.cssText = 'border-bottom:1px solid var(--border-lt);background:rgba(201,168,76,.03)';
        const atd1 = document.createElement('td'); atd1.style.cssText = 'padding:4px 10px 4px 24px;font-family:var(--mn);font-size:12px;color:var(--tx3)'; atd1.textContent = a.analytic_code || '';
        const atd2 = document.createElement('td'); atd2.style.cssText = 'padding:4px 10px;font-size:12px;color:var(--acc)'; atd2.textContent = '\u2514 ' + (a.analytic_name || '');
        const atd3 = document.createElement('td'); atd3.style.cssText = 'padding:4px 10px;text-align:right;font-family:var(--mn);font-size:12px';
        if (a.debit > 0) { atd3.textContent = fNum(a.debit); atd3.style.color = 'var(--dng)'; }
        const atd4 = document.createElement('td'); atd4.style.cssText = 'padding:4px 10px;text-align:right;font-family:var(--mn);font-size:12px';
        if (a.credit > 0) { atd4.textContent = fNum(a.credit); atd4.style.color = 'var(--suc)'; }
        atr.appendChild(atd1); atr.appendChild(atd2); atr.appendChild(atd3); atr.appendChild(atd4);
        tbody.appendChild(atr);
      });
    }
  });

  // Total row
  const ttr = document.createElement('tr'); ttr.style.cssText = 'background:var(--surface);font-weight:700;border-top:2px solid var(--border);position:sticky;bottom:0';
  const tt1 = document.createElement('td'); tt1.colSpan = 2; tt1.style.cssText = 'padding:8px 10px;font-size:14px'; tt1.textContent = 'UKUPNO (' + rows.length + ' konta)';
  const tt3 = document.createElement('td'); tt3.style.cssText = 'padding:8px 10px;text-align:right;font-family:var(--mn);color:var(--dng);font-size:15px'; tt3.textContent = fNum(grandD);
  const tt4 = document.createElement('td'); tt4.style.cssText = 'padding:8px 10px;text-align:right;font-family:var(--mn);color:var(--suc);font-size:15px'; tt4.textContent = fNum(grandC);
  ttr.appendChild(tt1); ttr.appendChild(tt3); ttr.appendChild(tt4);
  tbody.appendChild(ttr);
  tbl.appendChild(tbody); box.appendChild(tbl);
}

// ============================================================
// SEKCIJA 3: FINANSIJSKI PREGLED
// ============================================================
async function loadFinancialData() {
  finBsData = null; finBs12 = null;
  if (!state.CU) return;
  const year = parseInt(document.getElementById('dashYear')?.value) || new Date().getFullYear();
  const selMonth = parseInt(document.getElementById('dashMonth')?.value) || 0;
  const kurs = parseFloat(document.getElementById('cfKurs')?.value) || 108;
  try {
    const promises = [];
    for (let m = 1; m <= 12; m++) {
      promises.push(sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: m, p_kurs: kurs }));
    }
    const results = await Promise.all(promises);
    const monthsData = results.map(r => (!r.error && r.data) ? r.data : []);
    finBs12 = monthsData;
    // Use selected month if set, otherwise latest non-empty
    if (selMonth > 0 && monthsData[selMonth - 1]?.length) {
      finBsData = monthsData[selMonth - 1];
    } else {
      for (let m = 11; m >= 0; m--) {
        if (monthsData[m]?.length) { finBsData = monthsData[m]; break; }
      }
    }
  } catch (e) { console.error('Dashboard fin data:', e); }
}

function renderFinKpis() {
  const bar = document.getElementById('dashFinKpi'); if (!bar) return;
  bar.textContent = '';
  if (!finBsData || !finBsData.length) {
    const p = document.createElement('div'); p.style.cssText = 'grid-column:1/-1;color:var(--tx3);font-size:13px;text-align:center;padding:16px';
    p.textContent = 'Nema finansijskih podataka. Proverite da li postoje knjiženja.'; bar.appendChild(p); return;
  }
  const kurs = parseFloat(document.getElementById('cfKurs')?.value) || 108;
  const calc = (test) => finBsData.filter(r => test(r.konto)).reduce((s, r) => s + parseFloat(r.saldo || 0), 0);

  const cash = calc(k => k.startsWith('241') || k.startsWith('244'));
  const recv = calc(k => k.startsWith('204') || k.startsWith('205') || k.startsWith('200') || k.startsWith('201') || k.startsWith('202') || k.startsWith('203'));
  const payables = -calc(k => k.startsWith('435') || k.startsWith('436'));
  // Full class totals for accurate bilans
  const totalAssets = calc(k => k.startsWith('0') || k.startsWith('1') || k.startsWith('2'));
  const totalLiab = -calc(k => k.startsWith('4'));
  const totalEquity = -calc(k => k.startsWith('3'));
  const revenue = -calc(k => k.startsWith('6'));
  const expenses = calc(k => k.startsWith('5'));
  const netIncome = revenue - expenses;
  // Determine months with data for accurate burn rate average
  const selM = parseInt(document.getElementById('dashMonth')?.value) || 0;
  const monthsWithData = selM > 0 ? 1 : (finBs12 ? finBs12.filter(m => m && m.length > 0).length : 1);
  const burnRate = monthsWithData > 0 ? Math.round(expenses / monthsWithData) : expenses;
  const cl7 = calc(k => k.startsWith('7'));
  const checkBilans = totalAssets - totalLiab - totalEquity - netIncome + cl7;

  [{l:'Cash Position',v:fNum(cash),c:'var(--suc)',sub:fNum(cash/kurs)+' USD'},
   {l:'Potraživanja',v:fNum(recv),c:'var(--acc)',sub:'Kupci'},
   {l:'Dugovanja',v:fNum(payables),c:'var(--dng)',sub:'Dobavljači'},
   {l:'Neto Dobit',v:fNum(netIncome),c:netIncome>=0?'var(--suc)':'var(--dng)',sub:'Tekući period'},
   {l:'Burn Rate',v:fNum(burnRate),c:'var(--warn)',sub:'Prosečni mesečni rashodi'},
   {l:'Bilans Kontrola',v:Math.abs(checkBilans)<1?'OK':'RAZLIKA',c:Math.abs(checkBilans)<1?'var(--suc)':'var(--dng)',sub:'A=L+E+P&L: '+fNum(checkBilans)},
  ].forEach(s => {
    const card = document.createElement('div'); card.className = 'card';
    card.style.cssText = 'padding:14px 16px;display:flex;flex-direction:column;gap:3px';
    const lbl = document.createElement('div'); lbl.style.cssText = 'font-size:10px;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px'; lbl.textContent = s.l;
    const val = document.createElement('div'); val.style.cssText = 'font-size:18px;font-weight:700;font-family:var(--mn)'; val.textContent = s.v; if (s.c) val.style.color = s.c;
    const sub = document.createElement('div'); sub.style.cssText = 'font-size:10px;color:var(--tx3)'; sub.textContent = s.sub || '';
    card.appendChild(lbl); card.appendChild(val); card.appendChild(sub); bar.appendChild(card);
  });
}

function renderCashTrend() {
  const canvas = document.getElementById('dashCashChart'); if (!canvas) return;
  if (cashChartInst) cashChartInst.destroy(); cashChartInst = null;
  if (!finBs12) return;
  const cashData = finBs12.map(mData =>
    (mData || []).filter(r => r.konto.startsWith('241') || r.konto.startsWith('244')).reduce((s, r) => s + parseFloat(r.saldo || 0), 0)
  );
  const recvData = finBs12.map(mData =>
    (mData || []).filter(r => r.konto.startsWith('204') || r.konto.startsWith('205') || r.konto.startsWith('200') || r.konto.startsWith('201') || r.konto.startsWith('202') || r.konto.startsWith('203')).reduce((s, r) => s + parseFloat(r.saldo || 0), 0)
  );
  const payData = finBs12.map(mData =>
    -(mData || []).filter(r => r.konto.startsWith('435') || r.konto.startsWith('436')).reduce((s, r) => s + parseFloat(r.saldo || 0), 0)
  );
  cashChartInst = new Chart(canvas, {
    type: 'line', data: { labels: MONTHS, datasets: [
      { label: 'Cash (241+244)', data: cashData, borderColor: '#00e89d', backgroundColor: 'rgba(0,232,157,.1)', fill: true, tension: .3, pointRadius: 5, pointBackgroundColor: '#00e89d', pointBorderColor: '#0a0b10', pointBorderWidth: 2, borderWidth: 2.5 },
      { label: 'Potraživanja (200-205)', data: recvData, borderColor: '#c9a84c', backgroundColor: 'rgba(201,168,76,.1)', fill: true, tension: .3, pointRadius: 4, pointBackgroundColor: '#c9a84c', pointBorderColor: '#0a0b10', pointBorderWidth: 2, borderWidth: 2 },
      { label: 'Dugovanja (435+436)', data: payData, borderColor: '#ff5c6a', backgroundColor: 'rgba(255,92,106,.1)', fill: true, tension: .3, pointRadius: 4, pointBackgroundColor: '#ff5c6a', pointBorderColor: '#0a0b10', pointBorderWidth: 2, borderWidth: 2 },
    ] },
    options: { responsive: true, maintainAspectRatio: false, interaction: { intersect: false, mode: 'index' },
      scales: { y: { beginAtZero: true, ticks: { color: '#5d6180', font: { size: 12 }, callback: v => v>=1e9?(v/1e9).toFixed(1)+'B':v>=1e6?(v/1e6).toFixed(1)+'M':v>=1e3?(v/1e3).toFixed(0)+'K':v }, grid: { color: 'rgba(30,32,48,.5)' } }, x: { ticks: { color: '#9da1b8', font: { size: 12 } }, grid: { display: false } } },
      plugins: { legend: { position: 'top', labels: { color: '#9da1b8', font: { size: 12 }, padding: 12, boxWidth: 14 } }, tooltip: { callbacks: { label: ctx => ctx.dataset.label+': '+fNum(ctx.parsed.y)+' RSD' } } } }
  });
}

function renderTop10(containerId, kontoPrefix, title, color) {
  const box = document.getElementById(containerId); if (!box) return;
  box.textContent = '';
  if (!finBsData || !finBsData.length) {
    const p = document.createElement('div'); p.style.cssText = 'color:var(--tx3);font-size:13px;text-align:center;padding:16px';
    p.textContent = 'Nema podataka.'; box.appendChild(p); return;
  }
  // Filter konta matching prefix(es) and sort by absolute saldo
  const prefixes = kontoPrefix.split(',');
  const items = finBsData.filter(r => prefixes.some(p => r.konto.startsWith(p)))
    .sort((a, b) => Math.abs(parseFloat(b.saldo || 0)) - Math.abs(parseFloat(a.saldo || 0)))
    .slice(0, 10);

  if (!items.length) {
    const p = document.createElement('div'); p.style.cssText = 'color:var(--tx3);font-size:13px;text-align:center;padding:16px';
    p.textContent = 'Nema podataka za konto ' + kontoPrefix; box.appendChild(p); return;
  }

  const maxVal = Math.max(...items.map(r => Math.abs(parseFloat(r.saldo || 0))), 1);
  items.forEach((r, i) => {
    const saldo = Math.abs(parseFloat(r.saldo || 0));
    const pct = (saldo / maxVal * 100);
    const row = document.createElement('div'); row.style.cssText = 'display:flex;align-items:center;gap:10px;margin-bottom:8px';
    const num = document.createElement('div'); num.style.cssText = 'width:20px;font-size:11px;font-weight:700;color:var(--tx3);text-align:right'; num.textContent = (i + 1) + '.';
    const info = document.createElement('div'); info.style.cssText = 'flex:1;min-width:0';
    const name = document.createElement('div'); name.style.cssText = 'font-size:13px;color:var(--tx);white-space:nowrap;overflow:hidden;text-overflow:ellipsis'; name.textContent = r.naziv || r.konto;
    const barWrap = document.createElement('div'); barWrap.style.cssText = 'height:6px;background:var(--surface);border-radius:3px;margin-top:3px;overflow:hidden';
    const barEl = document.createElement('div'); barEl.style.cssText = 'height:100%;border-radius:3px;background:' + color + ';width:' + pct + '%;opacity:.7';
    barWrap.appendChild(barEl);
    info.appendChild(name); info.appendChild(barWrap);
    const val = document.createElement('div'); val.style.cssText = 'font-family:var(--mn);font-size:13px;font-weight:600;white-space:nowrap;color:' + color; val.textContent = fNum(saldo);
    row.appendChild(num); row.appendChild(info); row.appendChild(val); box.appendChild(row);
  });
}

// ============================================================
// AKTIVNOST
// ============================================================
async function renderActivity() {
  const box = document.getElementById('dashActivity'); if (!box) return;
  box.textContent = '';
  try {
    const { data, error } = await sb.from('audit_log').select('*').order('created_at', { ascending: false }).limit(10);
    if (error) throw error;
    if (!data||!data.length) { const p = document.createElement('div'); p.style.cssText = 'color:var(--tx3);font-size:13px;padding:12px 0'; p.textContent = 'Nema aktivnosti.'; box.appendChild(p); return; }
    const map = {'login':'Prijava','logout':'Odjava','delete':'Brisanje','export_excel':'Excel export','backup':'Backup','restore':'Restore','save_knjizenja':'Knjiženja','password_change':'Lozinka'};
    data.forEach(a => {
      const row = document.createElement('div'); row.style.cssText = 'display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px solid var(--border-lt);font-size:12px';
      const left = document.createElement('div'); left.style.cssText = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1';
      const who = document.createElement('span'); who.style.cssText = 'color:var(--acc);font-weight:600;margin-right:6px'; who.textContent = a.user_name||'System';
      const what = document.createElement('span'); what.style.color = 'var(--tx2)'; what.textContent = (map[a.action]||a.action)+(a.entity_name?' \u2014 '+a.entity_name:'');
      left.appendChild(who); left.appendChild(what);
      const when = document.createElement('div'); when.style.cssText = 'color:var(--tx3);white-space:nowrap;margin-left:8px;font-size:11px';
      when.textContent = new Date(a.created_at).toLocaleString('sr-RS',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'});
      row.appendChild(left); row.appendChild(when); box.appendChild(row);
    });
  } catch (e) { box.textContent = 'Greška.'; box.style.color = 'var(--tx3)'; }
}
